import { useId, CSSProperties } from 'react'

interface CuvetteProps {
  fillLevel?: number // 0-100
  fillColor?: string
  label?: string
  isInHolder?: boolean
  active?: boolean
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const Cuvette = ({
  fillLevel = 0,
  fillColor = '#7DD3FC',
  label = '',
  isInHolder = false,
  active = false,
  onClick,
  disabled = false,
  className = ''
}: CuvetteProps) => {
  const id = useId()

  // Cuvette dimensions (1cm path length, square cross-section)
  const width = 80
  const height = 120

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  // Calculate fill height
  const cuvetteTop = 10
  const cuvetteBottom = 65
  const cuvetteLeft = 12
  const cuvetteRight = 38
  const innerHeight = cuvetteBottom - cuvetteTop - 5
  const fillHeight = (fillLevel / 100) * innerHeight
  const fillTop = cuvetteBottom - 3 - fillHeight

  return (
    <svg
      viewBox="0 0 50 80"
      width={width}
      height={height}
      onClick={disabled ? undefined : onClick}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Glass gradient - horizontal for 3D look */}
        <linearGradient id={`cuvette-glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(180,210,230,0.7)" />
          <stop offset="15%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="30%" stopColor="rgba(220,240,255,0.2)" />
          <stop offset="70%" stopColor="rgba(220,240,255,0.2)" />
          <stop offset="85%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="100%" stopColor="rgba(180,210,230,0.6)" />
        </linearGradient>

        {/* Liquid gradient */}
        <linearGradient id={`cuvette-liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.85" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.95" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`cuvette-shadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.3" />
        </filter>

        {/* Active glow */}
        <filter id={`cuvette-glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Active glow ring */}
      {active && (
        <rect
          x={cuvetteLeft - 5}
          y={cuvetteTop - 5}
          width={cuvetteRight - cuvetteLeft + 10}
          height={cuvetteBottom - cuvetteTop + 10}
          rx="3"
          fill="none"
          stroke="var(--primary, #FF5A36)"
          strokeWidth="2"
          filter={`url(#cuvette-glow-${id})`}
        >
          <animate
            attributeName="stroke-opacity"
            values="1;0.5;1"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </rect>
      )}

      {/* Cuvette holder (if in spectrophotometer) */}
      {isInHolder && (
        <rect
          x={cuvetteLeft - 3}
          y={cuvetteTop + 15}
          width={cuvetteRight - cuvetteLeft + 6}
          height={cuvetteBottom - cuvetteTop - 10}
          fill="#1A1A1A"
          stroke="#3A3A3A"
          strokeWidth="1"
          rx="2"
        />
      )}

      {/* Main cuvette body */}
      <g filter={!isInHolder ? `url(#cuvette-shadow-${id})` : undefined}>
        {/* Back panel (depth effect) */}
        <rect
          x={cuvetteLeft}
          y={cuvetteTop}
          width={cuvetteRight - cuvetteLeft}
          height={cuvetteBottom - cuvetteTop}
          fill="rgba(200,220,240,0.15)"
          rx="1"
        />

        {/* Glass body */}
        <rect
          x={cuvetteLeft}
          y={cuvetteTop}
          width={cuvetteRight - cuvetteLeft}
          height={cuvetteBottom - cuvetteTop}
          fill={`url(#cuvette-glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="1.5"
          rx="1"
        />

        {/* Frosted top section (where you hold it) */}
        <rect
          x={cuvetteLeft}
          y={cuvetteTop}
          width={cuvetteRight - cuvetteLeft}
          height="12"
          fill="rgba(200,210,220,0.5)"
          rx="1"
        />
        <line
          x1={cuvetteLeft}
          y1={cuvetteTop + 12}
          x2={cuvetteRight}
          y2={cuvetteTop + 12}
          stroke="rgba(150,170,190,0.5)"
          strokeWidth="0.5"
        />

        {/* Liquid */}
        {fillLevel > 0 && (
          <g>
            <rect
              x={cuvetteLeft + 2}
              y={fillTop}
              width={cuvetteRight - cuvetteLeft - 4}
              height={fillHeight}
              fill={`url(#cuvette-liquid-${id})`}
              rx="0.5"
            />
            {/* Liquid surface highlight */}
            <ellipse
              cx={(cuvetteLeft + cuvetteRight) / 2}
              cy={fillTop}
              rx={(cuvetteRight - cuvetteLeft - 6) / 2}
              ry="1.5"
              fill={fillColor}
              opacity="0.9"
            />
            {/* Surface reflection */}
            <rect
              x={cuvetteLeft + 4}
              y={fillTop + 1}
              width="6"
              height="1"
              fill="rgba(255,255,255,0.4)"
              rx="0.5"
            />
          </g>
        )}

        {/* Glass highlights */}
        <rect
          x={cuvetteLeft + 2}
          y={cuvetteTop + 14}
          width="2"
          height={cuvetteBottom - cuvetteTop - 20}
          fill="rgba(255,255,255,0.5)"
          rx="1"
        />
        <rect
          x={cuvetteLeft + 5}
          y={cuvetteTop + 18}
          width="1"
          height={cuvetteBottom - cuvetteTop - 28}
          fill="rgba(255,255,255,0.3)"
          rx="0.5"
        />

        {/* Bottom interior (depth) */}
        <rect
          x={cuvetteLeft + 2}
          y={cuvetteBottom - 4}
          width={cuvetteRight - cuvetteLeft - 4}
          height="2"
          fill="rgba(150,180,200,0.3)"
          rx="0.5"
        />
      </g>

      {/* Label */}
      {label && (
        <text
          x="25"
          y="75"
          fontSize="8"
          fill="var(--text-primary, #FFFFFF)"
          textAnchor="middle"
          fontWeight="500"
          fontFamily="Arial, sans-serif"
        >
          {label}
        </text>
      )}
    </svg>
  )
}

export default Cuvette
